import React, { useState, useEffect } from 'react';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import Aside from './Aside';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import Swal from 'sweetalert2';

const HelpPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });

    const encryptId = (id) => {
        const secretKey = '12345';
        const encrypted = CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
        const sanitizedEncrypted = encrypted.replace(/\//g, '');
        return sanitizedEncrypted;
    };

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        try {
            const authData = localStorage.getItem('auth');
            const storedName = authData ? JSON.parse(authData).name : null;
            const storedEmail = authData ? JSON.parse(authData).email : null;
            setFormData((prevData) => ({
                ...prevData,
                name: storedName,
                email: storedEmail,
            }));
        } catch (error) {
            console.error('Error retrieving data from localStorage:', error);
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const [openFAQs, setOpenFAQs] = useState([0]);

    const toggleFAQ = (index) => {
        setOpenFAQs((prevOpenFAQs) => {
            if (prevOpenFAQs.includes(index)) {
                return prevOpenFAQs.filter((faqIndex) => faqIndex !== index);
            } else {
                return [...prevOpenFAQs, index];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const newErrors = {};
        if (!formData.subject) newErrors.subject = 'Subject is required.';
        if (!formData.message) newErrors.message = 'Message is required.';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const authData = localStorage.getItem('auth');
            const token = authData ? JSON.parse(authData).token : null;
            const user_id = authData ? JSON.parse(authData).id : null;

            if (!token) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    token: 'You must be logged in to send a message',
                }));
                console.error('Token not found in localStorage');
                setLoading(false);
                return;
            }
            if (!user_id) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    user_id: 'You must be logged in to send a message',
                }));
                console.error('User ID not found in localStorage');
                setLoading(false);
                return;
            }
            const encryptedId = encryptId(user_id);
            // formData.user_id = encryptedId;
            formData.user_id = user_id;
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/contact/`,
                {
                    ...formData,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + token,
                    },
                }
            );

            if (response.data.status === 200) {
                Swal.fire({
                    title: 'Success!',
                    text: response.data.message,
                    icon: 'success',
                    confirmButtonText: 'Ok'
                }).then(() => {
                    setFormData({
                        subject: '',
                        message: '',
                    });
                });
            } else if (response.data.status === 422) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    subject: response.message.subject ? response.message.subject[0] : '',
                    message: response.message.message ? response.message.message[0] : '',
                }));
            } else if (response.data.status === 401) {
                navigate('/login');
            }

        } catch (error) {
            console.error('Error during API call:', error);
        } finally {
            setLoading(false);
        }
    };

    const faqs = [
        {
            question: 'What is Hey Visuals?',
            answer: 'Hey Visuals is an AI-powered tool designed for social media content creators. It helps users generate high-quality captions, images, and avatars, enabling consistent posting and reducing design costs.',
        },
        {
            question: 'What features does Hey Visuals offer?',
            answer: (
                <>
                    Hey Visuals offers three primary features: <br />
                    <b> Caption Generation: </b> Create engaging and creative captions tailored to your content. <br />
                    <b> Image Generation:</b> Generate visually stunning images for your posts. <br />
                    <b> Avatar Maker:</b> Design custom avatars for branding or personal use.
                </>
            ),
        },
        {
            question: ' How does the Basic Plan differ from the Professional Plan?',
            answer: 'The Basic Plan provides up to 50 captions, 20 images, and 5 avatars per month, along with access to a standard library and priority email support. The Professional Plan offers unlimited access to captions, images, and avatars, a premium library, team collaboration (up to 3 members), and analytics insights.',
        },
        {
            question: ' Can I upgrade or downgrade my subscription plan?',
            answer: 'Yes, you can upgrade or downgrade your subscription plan anytime through your account settings. Changes will take effect at the start of the next billing cycle',
        },
        {
            question: 'Are there any additional costs or add-ons?',
            answer: 'There is no hidden extra cost.',
        },
        {
            question: 'What payment methods do you accept?',
            answer: 'TWe accept major credit cards and debit cards. All payments are processed securely.',
        },
        {
            question: 'Is there a refund policy?',
            answer: (<>
                We offer a 30-day money-back guarantee for the Basic and Professional Plans. If you're unsatisfied, contact our support team for a refund. </>),
        },
        {
            question: 'How can I contact customer support?',
            answer: (<>
                You can reach our support team via: <br />
                <b> Email:</b> <a href="mailto:support@heyvisuals.com"> support@heyvisuals.com </a> <br />
                <b> Live Chat:</b> Available for Basic and Professional Plan users. <br />
                Community forums and help center for Free Plan users.
            </>
            ),
        },
        {
            question: 'Can I cancel my subscription at any time?',
            answer: 'Yes, you can cancel your subscription anytime through your account settings. Your access will remain active until the end of the current billing period.',
        },
        {
            question: 'Are the AI-generated images copyright-free?',
            answer: 'Yes, all AI-generated images created using Hey Visuals are copyright-free and can be used for personal or commercial purposes.',
        },
        {
            question: 'Is Hey Visuals suitable for businesses?',
            answer: 'Absolutely! Hey Visuals is ideal for influencers, small businesses, and marketing teams looking to streamline content creation and reduce costs.',
        },
        {
            question: 'Do I need design skills to use Hey Visuals?',
            answer: 'No design skills are required! Hey Visuals is user-friendly and designed to make content creation easy for everyone.',
        },

        {
            question: 'How can I change the currency for my plans?',
            answer: (
                <>
                    First, click on your profile icon, then go to "Account Settings". Inside Account Settings, please select your country and timezone. The currency will automatically change according to your country.
                    <br />
                    <br />
                    <em>
                        <div className="text-danger col-md-12 fs-1x">
                            <b>Note:-</b> After changing the plan currency, if you want to purchase any paid plan, you must first take the free plan in that currency. You cannot directly purchase a paid plan after changing the currency.
                        </div>
                    </em>
                </>
            ),
        },
    ];

    return (
        <>
            <div>
                <Header />
                <div style={{ display: 'flex' }}>
                    <div style={{ zIndex: '101', background: 'white', boxShadow: '0 .8rem 1rem rgba(0, 0, 0, .15)', position: 'fixed', height: '100vh', }}>
                        <Aside />
                    </div>
                    <div className='mainstylle'>
                        <div className="help-page margintp" >
                            <section className="generate-main">
                                <div className="container py-4 px-2">
                                    <div className="row help">
                                        <div className="col-md-12">
                                            <div className="card rounded-4">
                                                <div className="card-body p-lg-17">
                                                    <form onSubmit={handleSubmit} className="form">
                                                        <h1 className="fw-bold text-dark mb-9 heading-50">Contact Us</h1>
                                                        <div className="row mb-5">
                                                            <div className="col-md-6">
                                                                <label className="fs-5 fw-semibold mb-2">
                                                                    Name <span className="required-field text-danger">*</span>
                                                                </label>
                                                                <input type="text" placeholder='Name' disabled className={`form-control form-control-solid contact-form-name-inp contact-form-inp ${errors.name ? 'is-invalid' : ''}`} name="name" value={formData.name} readOnly />
                                                                {errors.name && <span className="error-msgs text-danger">{errors.name}</span>}
                                                            </div>
                                                            <div className="col-md-6">
                                                                <label className="fs-5 fw-semibold mb-2">
                                                                    Email <span className="required-field text-danger">*</span>
                                                                </label>
                                                                <input type="email" placeholder='Email' disabled className={` form-control form-control-solid contact-form-email-inp contact-form-inp  ${errors.email ? 'is-invalid' : ''}`} name="email" value={formData.email} readOnly />
                                                                {errors.email && <span className="error-msgs text-danger">{errors.email}</span>}
                                                            </div>
                                                        </div>
                                                        <div className="mb-5">
                                                            <label className="fs-5 fw-semibold mb-2">
                                                                Subject <span className="required-field text-danger">*</span>
                                                            </label>
                                                            <input type="text" className={`form-control form-control-solid contact-form-subject-inp contact-form-inp  ${errors.subject ? 'is-invalid' : ''}`} placeholder='Enter subject here' name="subject" value={formData.subject} onChange={handleInputChange} />
                                                            {errors.subject && <span className="error-msgs text-danger">{errors.subject}</span>}
                                                        </div>
                                                        <div className="mb-10">
                                                            <label className="fs-6 fw-semibold mb-2">
                                                                Message <span className="required-field text-danger">*</span>
                                                            </label>
                                                            <textarea className={`form-control form-control-solid contact-form-message-inp contact-form-inp ${errors.message ? 'is-invalid' : ''}`} placeholder='Enter message here' rows="6" name="message" value={formData.message} onChange={handleInputChange} />
                                                            {errors.message && <span className="error-msgs text-danger">{errors.message}</span>}
                                                        </div>
                                                        <button type="submit" className="btn btn-theme btn-shadow" disabled={loading}>

                                                            {loading ? (
                                                                <>Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span></>
                                                            ) : (
                                                                "Send Feedback"
                                                            )}
                                                        </button>
                                                        {errors.token && <span className="error-msgs text-danger">{errors.token}</span>}
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <section className="faq-n-tutorials my-10">
                                        <div className="container py-4 px-2">
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="card rounded-4">
                                                        <div className="card-body p-10 p-lg-15">
                                                            <h1 className="fw-bold text-dark heading-50 mb-7">FAQ's</h1>
                                                            {faqs.map((faq, index) => (
                                                                <div key={index} className="m-0">
                                                                    <div
                                                                        className="d-flex align-items-center collapsible py-3 toggle"
                                                                        onClick={() => toggleFAQ(index)} >
                                                                        <div className="ms-n1 me-3">
                                                                            <i className={`ki-outline ki-${openFAQs.includes(index) ? 'down' : 'right'} toggle-on ${openFAQs.includes(index) ? 'text-primary' : 'text-muted'
                                                                                } fs-3`}></i>
                                                                        </div>
                                                                        <div className="d-flex align-items-center flex-wrap">
                                                                            <h3 className="text-gray-800 fw-semibold cursor-pointer fs-4 mb-0">
                                                                                {faq.question}
                                                                            </h3>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className={`faq-answer fs-6 ms-10 ${openFAQs.includes(index) ? 'open' : ''}`}
                                                                        style={{
                                                                            maxHeight: openFAQs.includes(index) ? '1000px' : '0',
                                                                            opacity: openFAQs.includes(index) ? '1' : '0',
                                                                        }} >
                                                                        <span className="text-muted fw-semibold fs-5">{faq.answer}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </section>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </>
    );
};

export default HelpPage;
